// Accept-invitation surface — ONE page mounted at all four emailed invite
// paths (the server's buildInviteUrl targets, previously unrouted 404s —
// the P0 invite dead-end, 2026-08-06):
//
//   /accept-invite            → PROJECT invitation      → sdk.acceptInvite
//   /accept-org-invite        → ORGANIZATION invitation → sdk.acceptOrgInvitation
//   /accept-team-invite       → TEAM invitation         → sdk.acceptTeamInvitation
//   /accept-workspace-invite  → WORKSPACE invitation    → sdk.acceptWorkspaceInvitation
//
// Flow: parse kind (from the path) + the token. Unauthed visitors are routed
// to /login?next=<this path, token in the fragment> so the token survives the
// sign-in round-trip (signin/LoginWindow already honor ?next). Authed visitors get
// the matching SDK accept call, a progress → success/error card, and on
// success the joined org is activated (sdk.switchOrg — same convention as
// AppCreateOrgModal) before a hard replace('/') re-keys claims + queries.
//
// Same LoginWindow-family chrome as the other auth pages: authPageTheme
// backdrop + brand ModalWindow card (ModalHeader's Close is dropped — a
// full-page surface has nothing to close to, see LoginWindow #2278).
//
// THE TOKEN STAYS IN MEMORY (INVITE-MAGIC-LINK-1 hardening, review
// 2026-10-09). It is a bearer credential: `#token=` on a magic link,
// `?token=` on an emailed invitation. The first script of
// workspace/index.html takes it out of the address bar on these paths before
// any other script runs (and sets Referrer-Policy no-referrer); this page
// takes it from there (`el.call('takeInviteToken')`) and keeps it in a local
// for the preview and accept POSTs. The way back after sign-in carries it in
// the FRAGMENT, which no server sees.
import { authPageTheme } from './theme.js'
const KIND_BY_PATH = {
  '/accept-invite': 'project',
  '/accept-org-invite': 'organization',
  '/accept-team-invite': 'team',
  '/accept-workspace-invite': 'workspace'
}

// Bundle KEYS, not copy — `kindLabel` is interpolated into several sentences
// and every render site resolves it through polyglot (which returns a non-key
// verbatim, so an older state value still reads).
const KIND_LABEL = {
  project: 'auth.acceptInvite.kind.project',
  organization: 'auth.acceptInvite.kind.organization',
  team: 'auth.acceptInvite.kind.team',
  workspace: 'auth.acceptInvite.kind.workspace'
}

// The kind in the CASE its sentence needs — one literal key per form. A
// Georgian case ending cannot be glued onto the nominative: "ამ {kind}-ის
// წევრი" read "ამ პროექტი-ის წევრი" (the genitive is პროექტის), "შეუერთდით
// {kind}-ს" read "სამუშაო სივრცე-ს". English forms equal the plain noun, so
// `{kind}` stays one placeholder in both languages
// (shared/__tests__/acceptInviteGeorgianCase.test.js).
const KIND_OF = {
  project: 'auth.acceptInvite.kindOf.project',
  organization: 'auth.acceptInvite.kindOf.organization',
  team: 'auth.acceptInvite.kindOf.team',
  workspace: 'auth.acceptInvite.kindOf.workspace'
}
const KIND_TO = {
  project: 'auth.acceptInvite.kindTo.project',
  organization: 'auth.acceptInvite.kindTo.organization',
  team: 'auth.acceptInvite.kindTo.team',
  workspace: 'auth.acceptInvite.kindTo.workspace'
}
const KIND_IN = {
  project: 'auth.acceptInvite.kindIn.project',
  organization: 'auth.acceptInvite.kindIn.organization',
  team: 'auth.acceptInvite.kindIn.team',
  workspace: 'auth.acceptInvite.kindIn.workspace'
}

// The token this page load took, for the path it was taken on — in memory
// for the life of the document, never in storage or the URL. It exists for
// ONE case: a REMOUNT of this page with no navigation. A warm start mounts an
// early shell before the session check; when that check finds no user, the
// boot disposes the early shell and mounts a fresh one (shared/functions/
// bootShell.js `_disposeEarlyShell`, guest routes only), and this page runs
// again — after the token already left the address bar. Dropped once the
// invitation is accepted.
//
// FA201 / frank trap (WS-SHARED-MODULE-LET-BLOCKS-EVERY-PUSH-1; gazer's push
// was refused on it 2026-10-18): this used to be a module-scope
// `let _heldInvite`, read by `_parseInviteUrl`. frank revives a page's
// functions with no module scope, so the read was an unresolvable identifier
// and the emission gate refused every project that bundles this page.
// Re-homed onto a NON-enumerable `globalThis.__acceptInviteHeld` (the
// pointerCapability.js template) and deleted the moment the invitation
// settles — still memory only, never storage or the URL.

// Read the invite kind from the CURRENT path and TAKE the token (query or
// fragment) out of the address bar into memory. Reading window.location
// directly is the established auth-page convention here (signin/signup do
// the same for ?next / ?token) — these pages own the entry URL by definition.
const _parseInviteUrl = (el) => {
  if (typeof window === 'undefined') return { kind: null, token: null }
  const url = new URL(window.location.href)
  const path = url.pathname.replace(/\/+$/, '') || '/'
  const taken = el.call('takeInviteToken') || null
  if (taken) {
    Object.defineProperty(globalThis, '__acceptInviteHeld', {
      value: { path, token: taken },
      writable: true,
      configurable: true,
      enumerable: false
    })
  }
  const heldInvite = globalThis.__acceptInviteHeld
  const held = heldInvite && heldInvite.path === path ? heldInvite.token : null
  return { kind: KIND_BY_PATH[path] || null, token: taken || held }
}

// The way back to this page after sign-in / sign-up: this path, the live
// query (never a token) and the token in the FRAGMENT — the landing after
// sign-in never sends it to a server, and this page takes it out of the
// address bar again on arrival.
const _returnPath = (token) => {
  const url = new URL(window.location.href)
  url.searchParams.delete('token')
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''))
  hash.set('token', token)
  return url.pathname + url.search + '#' + hash.toString()
}

const _acceptByKind = (el, kind, token) => {
  if (kind === 'organization') return el.sdk.acceptOrgInvitation({ token })
  if (kind === 'team') return el.sdk.acceptTeamInvitation({ token })
  if (kind === 'workspace') return el.sdk.acceptWorkspaceInvitation({ token })
  return el.sdk.acceptInvite(token)
}

// Personalized invite context (INVITE-JOIN-PAGE-PERSONALIZED-1) — the
// public preview endpoint names the workspace/org/inviter for a token so
// this page can say "Join Acme" instead of the generic word "invitation".
// FEATURE-DETECTED: older SDK/server builds return null and every caller
// degrades to the previous generic copy. Never throws.
const _fetchPreview = async (el, token) => {
  try {
    if (typeof el.sdk?.getInvitationPreview !== 'function') return null
    const res = await el.sdk.getInvitationPreview({ token })
    const p = res?.data || res || null
    if (!p || typeof p !== 'object') return null
    return {
      status: p.status || 'pending',
      workspaceName: p.workspaceName || '',
      orgName: p.orgName || p.organizationName || '',
      inviterName: p.inviterName || '',
      inviteeEmail: p.inviteeEmail || p.email || '',
      role: p.role || '',
      // INVITE-MAGIC-LINK-1 — false only for an org magic link (bound to no
      // email); an older server sends nothing, which reads as email-bound.
      emailBound: p.emailBound !== false
    }
  } catch (_) {
    return null
  }
}

// What the invite is asking the user to join, for headlines/copy — the most
// specific name the preview carries, else '' so every render site takes its
// generic-copy branch. The fallback used to be `the ${label}` — and `label` is
// a bundle KEY, so a preview without names headlined "Join the
// auth.acceptInvite.kind.project".
const _joinTargetName = (preview) => preview?.workspaceName || preview?.orgName || ''

// What the HEADING names: the ORGANIZATION, for every invite kind (Nika
// 2026-10-14: "instead of 'Join Default' we say 'Join orgname'" — an org's
// default workspace is literally named "Default"). The body copy keeps the
// workspace name (`targetName`) where it adds information.
const _joinHeadName = (s) => s.orgName || s.targetName || ''

// Which org did this invite land the user in? (Used to activate it so the
// user wakes up INSIDE the org they just joined.)
const _resolveJoinedOrgId = async (el, kind, data) => {
  try {
    if (kind === 'organization') return data?.member?.organization || null
    if (kind === 'team') return data?.organizationId || null
    if (kind === 'workspace') {
      // The accept response carries the org id directly (bare ObjectId);
      // the getWorkspace round-trip is only the older-server fallback.
      if (data?.organization) return String(data.organization)
      const wsId = data?.workspace
      if (!wsId) return null
      const ws = await el.sdk.getWorkspace(String(wsId))
      return ws?.organization || ws?.org_id || null
    }
  } catch (_) {
    /* org activation is best-effort — the membership already exists */
  }
  return null
}

// Surface the joined org + workspace in the switcher's data sources
// BEFORE the switch commits, so the shell renders them the moment the
// switch pipeline rebuilds — the same optimistic-merge convention
// AppOrgSwitcher's "+ New workspace" uses for root.orgWorkspaces. Shapes
// mirror the canonical writers: root.orgs rows come from /auth/me
// user.organizations ({ id, slug, name, type, role }); orgWorkspaces rows
// are the normalized SDK-prefetch shape. The next auth tick re-derives
// both from the server, so a minimal row here can only be corrected,
// never fought.
// Root STATE, never the root ELEMENT: `el.getRoot().update({...})` writes an
// element prop (measured live 2026-08-27 on dev — the flag landed on
// `root._usernamePromptPending`, `root.state` never saw it, so
// UsernamePromptGate / NoOrgOverlay, which read `s.root`, never fired and the
// switcher never learned the joined org). Same resolver as
// functions/usernamePrompt.js `_rootState`.
const _rootState = (el) =>
  el?.state?.root || el?.getRoot?.()?.state?.root || el?.getRoot?.()?.state || null

const _mergeJoinedIntoState = async (el, kind, data, orgId) => {
  const root = _rootState(el)
  if (!root || !orgId) return null
  const patch = {}
  const orgs = Array.isArray(root.orgs) ? root.orgs : []
  if (!orgs.some((o) => String(o?.id || o?._id) === String(orgId))) {
    patch.orgs = [
      ...orgs,
      {
        id: String(orgId),
        slug: data?.organizationSlug || '',
        name: data?.organizationName || data?.organizationSlug || 'Organization',
        // The org's REAL type, off the accept response
        // (WS-INVITE-LIVE-SWITCH-SKIPS-PERSONAL-ORG-1). This row used to be
        // hardcoded `'team'`, which is wrong for the commonest case there is:
        // an invite sent from a personal account. Null on an older server —
        // the next auth tick re-derives the row from /auth/me either way.
        type: data?.organizationType || null,
        role: 'contributor'
      }
    ]
  }
  const wsId = kind === 'workspace' && data?.workspace ? String(data.workspace) : null
  if (wsId) {
    let ws = null
    try {
      ws = await el.sdk.getWorkspace(wsId)
    } catch (_) {
      ws = null
    }
    const byOrg = { ...(root.orgWorkspaces || {}) }
    const rows = Array.isArray(byOrg[orgId]) ? byOrg[orgId] : []
    if (!rows.some((w) => String(w?.id || w?.workspace_id) === wsId)) {
      const displayName =
        ws?.displayName || ws?.name || data?.workspaceName || data?.workspaceSlug || 'Workspace'
      byOrg[orgId] = [
        ...rows,
        {
          id: wsId,
          workspace_id: wsId,
          org_id: String(orgId),
          organization: String(orgId),
          // The URL handle this row must be matchable BY. `findWorkspaceInOrg`
          // (and the shell's registerWorkspaceHandleResolver, which uses it)
          // matches slug → key → id in that order, so a row carrying only an id
          // could not claim the `/w/<org>/<slug-or-key>` path this page routes
          // to — segment 2 read as a route name and the deep link fell apart.
          slug: ws?.slug || data?.workspaceSlug || null,
          key: ws?.key || data?.workspaceKey || null,
          type: ws?.type || 'shared',
          displayName,
          name: displayName
        }
      ]
      patch.orgWorkspaces = byOrg
    }
  }
  if (Object.keys(patch).length) root.update(patch)
  return wsId
}

// Server error → human message. The SDK surfaces the server's error body
// as Error.message, so we classify on substrings. `link`: the invitation is a
// magic link, where "not pending" means someone already used it (or it was
// turned off) — say that, not the email invitation's "no longer active".
const _friendlyError = (el, kind, message, { link = false } = {}) => {
  const msg = String(message || '')
  const t = (key) => el.call('polyglot', key)
  if (link && /not pending/i.test(msg)) {
    return { text: t('auth.acceptInvite.linkUsed') }
  }
  if (/already a member/i.test(msg)) {
    return {
      already: true,
      text: t('auth.acceptInvite.alreadyMember').replace(
        '{kind}',
        t(KIND_OF[kind] || 'auth.acceptInvite.kindOf.invitation')
      )
    }
  }
  // The server's JWT-verify failure reads 'Invalid or expired token'
  // (genuinely ambiguous server-side) — classify it BEFORE the plain
  // /expired/ branch, which is reserved for the record-level
  // 'Invitation expired'.
  if (/invalid or expired/i.test(msg)) {
    return {
      text: t('auth.acceptInvite.invalidOrExpired')
    }
  }
  if (/expired/i.test(msg)) {
    return { text: t('auth.acceptInvite.expired') }
  }
  if (/not pending|revoked/i.test(msg)) {
    return { text: t('auth.acceptInvite.inactive') }
  }
  if (/email.?mismatch/i.test(msg)) {
    return {
      text: t('auth.acceptInvite.wrongEmail')
    }
  }
  if (/not found/i.test(msg)) {
    return { text: t('auth.acceptInvite.notFound') }
  }
  if (/invalid/i.test(msg)) {
    return { text: t('auth.acceptInvite.invalid') }
  }
  return { text: msg || t('auth.acceptInvite.generic') }
}

// The invitation is already settled for THIS visitor: "Already a member", or
// a spent invitation whose org the signed-in visitor belongs to (the sign-up
// path accepts inline, then sends them through sign-in back here). Success
// copy, then a fresh boot on '/' — with the joined org made active first when
// its id is known.
const _settleAlreadyMember = async (el, s, kind, orgId) => {
  delete globalThis.__acceptInviteHeld
  const friendly = _friendlyError(el, kind, 'Already a member')
  s.update({ status: 'success', message: friendly.text, dest: '/' })
  if (orgId) {
    try {
      await el.sdk.switchOrg(String(orgId))
    } catch (_) {
      /* activation is best-effort — the membership already exists */
    }
  }
  setTimeout(() => window.location.replace('/'), 900)
}

// The org an ORG or TEAM invitation is for, read off its token — for routing
// only; the server decides access (functions/inviteToken.js).
const _invitationOrgId = (el, token) => {
  const claims = el.call('inviteTokenClaims', token)
  return (claims && claims.organizationId) || null
}

// Does the signed-in visitor belong to `orgId`? /auth/me's
// user.organizations, then the shell's own org rows.
const _memberOfOrg = (el, me, orgId) => {
  const fromMe = me?.user?.organizations || me?.data?.user?.organizations || me?.organizations || []
  const fromRoot = (_rootState(el) || {}).orgs || []
  return [...fromMe, ...fromRoot].some((o) => String(o?.id || o?._id || '') === String(orgId))
}

const _runAcceptFlow = async (el, s) => {
  if (typeof window === 'undefined') return
  const { kind, token } = _parseInviteUrl(el)
  const label = KIND_LABEL[kind] || 'auth.acceptInvite.kind.invitation'
  s.update({
    kindLabel: label,
    kindInLabel: KIND_IN[kind] || 'auth.acceptInvite.kindIn.invitation'
  })
  // Tell the shell an invite flow owns this beat — NoOrgOverlay suppresses
  // its org-creation gate while this is up (an invitee is JOINING an org;
  // pushing them to create one was the reported confusion).
  try {
    _rootState(el)?.update({ _inviteFlowActive: true })
  } catch (_) {
    /* shell-less mounts (tests) have no root state to flag */
  }

  if (!token) {
    s.update({
      status: 'error',
      message: el.call('polyglot', 'auth.acceptInvite.missingToken')
    })
    return
  }

  // Auth gate — the page renders pre-auth (guest-allowlisted) so IT owns
  // the choice of path and the token survives via ?next=. getMe() is the
  // authoritative session probe (init-gated Promise, safe to await).
  // No session, no probe: with no stored token (or the shell's verdict is
  // "signed out"), /auth/me can only answer 401 — measured on the
  // white-label landing, a clean profile (2026-10-11). An SDK without
  // getAuthToken keeps the probe.
  // The verdict is isAuthorised()'s (methods/canvas.js): once the shell's
  // session pass is ready (root._authReady), root.signedIn follows an
  // in-place sign-in; the boot gate's answer stands only until then. An
  // invited sign-up's "Continue" opens the account in the page and routes
  // here under a boot that said "signed out" — reading the boot flag alone
  // showed that member the signed-out welcome (gita.pr, 2026-10-28).
  let me = null
  const _bootSignedIn = el.context?.bootSignedIn
  const _root = _rootState(el)
  const _signedIn =
    typeof _bootSignedIn === 'boolean' &&
    _root &&
    _root._authReady === true &&
    typeof _root.signedIn === 'boolean'
      ? _root.signedIn
      : _bootSignedIn
  const _noSession =
    _signedIn === false || (typeof el.sdk?.getAuthToken === 'function' && !el.sdk.getAuthToken())
  if (!_noSession) {
    try {
      me = await el.sdk.getMe()
    } catch (_) {
      me = null
    }
  }

  // Name the thing being joined as early as possible — both branches
  // below read it. Null on older builds → generic copy everywhere.
  const preview = await _fetchPreview(el, token)
  if (preview) {
    s.update({
      targetName: _joinTargetName(preview),
      orgName: preview.orgName,
      inviterName: preview.inviterName,
      inviteeEmail: preview.inviteeEmail,
      linkInvite: preview.emailBound === false
    })
    // A dead invite reads the same signed-in or signed-out — say so
    // before asking anyone to authenticate for it.
    if (preview.status === 'expired') {
      s.update({
        status: 'error',
        message: el.call('polyglot', 'auth.acceptInvite.expired')
      })
      return
    }
    if (preview.status === 'revoked') {
      s.update({
        status: 'error',
        message: el.call('polyglot', 'auth.acceptInvite.inactive')
      })
      return
    }
    // INVITE-MAGIC-LINK-1 — a SPENT invitation. Whoever spent it is usually
    // the one coming back: an invited sign-up (an emailed invitation, or a
    // link through a verified OAuth sign-in) accepts inline, then goes
    // through sign-in to this page. A signed-in visitor who already belongs
    // to the invitation's org is in — say so and open it, instead of an
    // accept call that can only answer "not pending". A magic link spent by
    // anyone else no longer works — say that before asking anyone to sign
    // in. An email invitation otherwise keeps its path below.
    if (preview.status === 'accepted') {
      const orgId = _invitationOrgId(el, token)
      if (me && orgId && _memberOfOrg(el, me, orgId)) {
        await _settleAlreadyMember(el, s, kind, orgId)
        return
      }
      if (preview.emailBound === false) {
        s.update({
          status: 'error',
          message: el.call('polyglot', 'auth.acceptInvite.linkUsed')
        })
        return
      }
    }
  }

  // Both signed-out actions and the wrong-account beat need these.
  const returnPath = _returnPath(token)
  const loginPath = '/login?next=' + encodeURIComponent(returnPath)
  const signupPath =
    '/signup?token=' + encodeURIComponent(token) + '&next=' + encodeURIComponent(returnPath)
  s.update({ loginPath, signupPath })

  if (!me) {
    // Signed-out invitee — the WELCOME beat (INVITE-JOIN-PAGE-
    // PERSONALIZED-1): a personalized join card offering account creation
    // (the common case — invites target new teammates) with sign-in as
    // the secondary path. The token rides to signup via ?token= (its
    // register flow verifies + auto-accepts it) and to sign-in via ?next=
    // back to this page. Older builds with no preview still get the
    // welcome card, just with generic copy.
    s.update({ status: 'welcome' })
    return
  }

  // Signed in as the WRONG account — say which email the invite wants
  // before burning the accept call on a guaranteed email_mismatch.
  const myEmail = String(me?.email || me?.data?.user?.email || me?.user?.email || '').toLowerCase()
  if (preview?.inviteeEmail && myEmail && preview.inviteeEmail.toLowerCase() !== myEmail) {
    s.update({
      status: 'mismatch',
      message: el
        .call('polyglot', 'auth.acceptInvite.mismatch')
        // Function replacers: an email local part may hold `$&` / `$1`.
        .replace('{invited}', () => preview.inviteeEmail)
        .replace('{current}', () => myEmail)
    })
    return
  }

  s.update({ status: 'accepting' })
  try {
    const data = await _acceptByKind(el, kind, token)
    const orgId = await _resolveJoinedOrgId(el, kind, data)

    // GA-W3.3 — a WORKSPACE invite lands the user INSIDE that workspace
    // (the accept response carries the org + workspace handles), never on
    // the generic dashboard.
    //
    // WS-INVITE-LIVE-SWITCH-SKIPS-PERSONAL-ORG-1 — this used to require
    // `workspaceSlug` and fall back to '/' without it, which is what every
    // real invite hit: `Workspace.slug` is OPTIONAL and null on every
    // workspace minted before slug allocation existed (143 of 143 under the
    // org measured on dev), so the deep link degraded to the dashboard for
    // the common case. The handle rule is `slug || key || id` — the SAME
    // precedence `workspaceUrlHandle` and `findWorkspaceInOrg` use, inlined
    // rather than imported (FA001/FA204: a bare sibling-export read is
    // unresolvable post-frank, and this page owns its own entry URL anyway).
    // '/' now means only "no org slug to build a path from at all".
    const wsHandle =
      kind === 'workspace'
        ? data?.workspaceSlug ||
          data?.workspaceKey ||
          (data?.workspace ? String(data.workspace) : '')
        : ''
    const dest =
      kind === 'workspace' && data?.organizationSlug && wsHandle
        ? `/w/${data.organizationSlug}/${wsHandle}`
        : '/'

    // LIVE activation (INVITE-ACCEPT-ACTIVATES-WORKSPACE-LIVE-1): merge the
    // joined org + workspace into the switcher's data sources, then commit
    // the switch through the same sdk.switchOrg pipeline the switcher's own
    // _commitOrgSwitch uses — its orgSwitched event drives the shell's
    // in-place wipe + refetch (epoch-guarded, tenant-scoped since the
    // 2026-07-18 switch fixes), so no page reload is needed.
    let switched = false
    if (orgId) {
      const wsId = await _mergeJoinedIntoState(el, kind, data, orgId)
      try {
        await el.sdk.switchOrg(String(orgId), wsId ? { homeWorkspaceId: wsId } : undefined)
        switched = true
      } catch (switchErr) {
        // Older servers reject the switch for workspace-kind invites (no
        // org membership row yet) — fall back to the hard-reload recovery.
        console.warn('[accept-invite] activate joined org failed:', switchErr?.message || switchErr)
      }
    }

    delete globalThis.__acceptInviteHeld
    s.update({
      status: 'success',
      message: el
        .call('polyglot', 'auth.acceptInvite.joined')
        .replace(
          '{kind}',
          el.call('polyglot', KIND_TO[kind] || 'auth.acceptInvite.kindTo.invitation')
        ),
      dest
    })
    // Queue the invitee's LIGHT onboarding step — the username nudge
    // (UsernamePromptGate opens it once after landing; the DONE key caps
    // it at one showing ever). Live path: root flag. Fallback path:
    // persisted marker, because the hard reload wipes root state.
    if (switched) {
      try {
        _rootState(el)?.update({ _usernamePromptPending: true, _inviteFlowActive: false })
      } catch (_) {
        /* flag is a nudge trigger — never worth failing the accept over */
      }
      // Paint the success beat, strip the consumed token from history
      // (query only — the router below performs the real path change, so
      // handing it an already-current path must stay impossible), then
      // SPA-route into the joined workspace. The switch pipeline has
      // already rebuilt state; a reload would only redo that work and
      // lose the no-reload acceptance this page now owns.
      try {
        window.history.replaceState({}, '', window.location.pathname)
      } catch (_) {
        /* history is cosmetic here — the router below still lands */
      }
      setTimeout(() => el.router(dest, el.getRoot()), 900)
    } else {
      try {
        el.call('persistUsernamePromptPending')
      } catch (_) {
        /* function registry absent (non-workspace shell) — nudge skipped */
      }
      // Recovery convention (AppCreateOrgModal / NoOrgOverlay): a fresh
      // boot re-keys every claim read + `method: 'select'` query.
      // replace() keeps the consumed-token URL out of history.
      setTimeout(() => window.location.replace(dest), 900)
    }
  } catch (err) {
    // Review D1 — a link joins only a VERIFIED account; an unverified one gets
    // 403 `email_not_verified` and the link stays open. Say what to do, in the
    // session language: the server's code, never its English message.
    if (err?.cause?.error === 'email_not_verified') {
      s.update({ status: 'error', message: el.call('polyglot', 'auth.acceptInvite.verifyFirst') })
      return
    }
    const friendly = _friendlyError(el, kind, err?.message, { link: s.linkInvite === true })
    if (friendly.already) {
      // Idempotent replay (double-click, refresh) — treat as success.
      _settleAlreadyMember(el, s, kind, null)
      return
    }
    console.warn('[accept-invite] accept failed:', err?.message || err)
    s.update({ status: 'error', message: friendly.text })
  }
}

export const acceptInvite = {
  extends: 'Page',
  // On a white-label host the tab names the org ("<page> / <org>"). While the
  // brand is not there, the page answers NO title: the edge-stamped one stays
  // while it loads, and the shell's route wrapper supplies its own title once
  // the host turns out to have no brand (shell functions/authWrapperTitle.js).
  // isCustomDomainHost is a shell function; elsewhere this is a platform page.
  metadata: (el) => {
    if (el.state?.root?.whitelabelBrand?.name) {
      return { title: el.call('pageTitle', 'auth.page.acceptInvite.title') }
    }
    const fns = el.context?.functions
    if (typeof fns?.isCustomDomainHost === 'function' && el.call('isCustomDomainHost')) {
      return { title: undefined }
    }
    return { title: el.call('pageTitle', 'auth.page.acceptInvite.title') }
  },
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  '@mobileL': {
    padding: 'A'
  },
  width: '100%',
  height: '100dvh',
  maxWidth: 'none',
  ...authPageTheme,

  ModalWindow: {
    // State + flow live ON the card, not the page: brand ModalWindow
    // declares `state: {}` (an isolated scope — see LoginWindow's note),
    // so bindings inside the card can only see state owned HERE. Initial
    // keys make every reactive factory below register its dep at first
    // render. `status`: checking → accepting → success | error (or →
    // redirect when unauthed).
    state: {
      status: 'checking',
      message: '',
      kindLabel: 'auth.acceptInvite.kind.invitation',
      kindInLabel: 'auth.acceptInvite.kindIn.invitation',
      dest: '/',
      // Personalization (preview-fed; empty on older builds → generic copy)
      targetName: '',
      orgName: '',
      inviterName: '',
      inviteeEmail: '',
      // INVITE-MAGIC-LINK-1 — the invitation is a magic link (bound to no
      // email): any account may accept it, once.
      linkInvite: false,
      // Signed-out welcome-beat destinations
      signupPath: '',
      loginPath: ''
    },

    onCreate: (el, s) => {
      _runAcceptFlow(el, s)
    },

    // The org's logo + name on a white-label host (the branded sign-in's
    // lockup, components/WhitelabelSignin.js); nothing on a platform host.
    WhitelabelLockup: {
      order: '-1',
      subscribeTo: ['whitelabelBrand'],
      hide: (el, s) => !(s.root && s.root.whitelabelBrand),
      // On this card the lockup sits on the design-system card ground, not
      // the branded sign-in's: its ink is the card's own (title / caption,
      // AA on that ground by the design system), and the name is not a
      // heading — the card's own heading stays the page's one h1.
      Wordmark: { tag: 'div', color: 'title' },
      Tagline: { color: 'caption' }
    },

    // Full-page card, not a floating dialog: neutralize the modal
    // positioning the same way LoginWindow does.
    position: 'relative',
    transform: 'none',
    top: 'auto',
    left: 'auto',
    margin: 'auto',
    border: 'none',
    boxShadow: 'none',
    width: '100%',
    maxWidth: 'I_default',
    minHeight: 'F',
    padding: 'B B2 C1',
    // White-label host: the branded sign-in's card measure and inset, so the
    // invitation reads as the same family as login and signup (Nika
    // 2026-10-14). Platform values above stay untouched.
    isBranded: (el, s) => !!(s.root && s.root.whitelabelBrand),
    '.isBranded': { maxWidth: 'overlaySm', minHeight: 'auto', padding: 'B1' },
    gap: 'B',
    align: 'center center',
    textAlign: 'center',
    // Drop the inherited header (its Close X has nothing to close to on a
    // full-page surface — see LoginWindow's #2278 note); this card renders
    // its own heading below.
    ModalHeader: null,

    // Progress overlay while we probe the session / call the accept
    // endpoint — same scrim treatment as LoginWindow's sign-in beat.
    LoadingGifSection: {
      background: 'scrim',
      position: 'absolute',
      inset: '0',
      fullSize: true,
      transition: 'B defaultBezier',
      transitionProperty: 'opacity, visibility',
      pointerEvents: (el, s) =>
        s.status === 'checking' || s.status === 'accepting' || s.status === 'redirect'
          ? 'auto'
          : 'none',
      opacity: (el, s) =>
        s.status === 'checking' || s.status === 'accepting' || s.status === 'redirect' ? 1 : 0,
      visibility: (el, s) =>
        s.status === 'checking' || s.status === 'accepting' || s.status === 'redirect'
          ? 'visible'
          : 'hidden'
    },

    // Override the brand Hgroup's OWN H + P children (adding new keys
    // would render beside the 'Heading'/'Paragraph' defaults, not replace
    // them).
    Hgroup: {
      gap: 'Y',
      H: {
        // The page's ONE h1 (the brand Hgroup defaults H to h3; the org
        // lockup's name above is a div on this card) — through the shared
        // title level, which answers h2 only under a pane title bar.
        tag: (el, s) => el.call('pageTitleTag', s),
        // White-label host: the branded cards' heading size (WhitelabelAuthWindow).
        isBranded: (el, s) => !!(s.root && s.root.whitelabelBrand),
        '.isBranded': { fontSize: 'B1' },
        text: (el, s) => {
          const t = (key) => el.call('polyglot', key)
          const head = _joinHeadName(s)
          if (s.status === 'welcome') {
            return head
              ? t('auth.acceptInvite.h.welcomeNamed').replace('{name}', () => head)
              : t('auth.acceptInvite.h.welcome')
          }
          if (s.status === 'mismatch') return t('auth.acceptInvite.h.mismatch')
          if (s.status === 'success') return t('auth.acceptInvite.h.success')
          if (s.status === 'error') return t('auth.acceptInvite.h.error')
          if (s.status === 'redirect') return t('auth.acceptInvite.h.redirect')
          return head
            ? t('auth.acceptInvite.h.joiningNamed').replace('{name}', () => head)
            : t('auth.acceptInvite.h.joining')
        }
      },
      P: {
        lineHeight: '1.5em',
        text: (el, s) => {
          const t = (key) => el.call('polyglot', key)
          if (s.status === 'welcome') {
            const who = s.inviterName
              ? t('auth.acceptInvite.p.whoNamed').replace('{name}', () => s.inviterName)
              : t('auth.acceptInvite.p.who')
            const where = s.targetName
              ? s.orgName && s.orgName !== s.targetName
                ? t('auth.acceptInvite.p.whereNamedOrg')
                    .replace('{name}', () => s.targetName)
                    .replace('{org}', () => s.orgName)
                : t('auth.acceptInvite.p.whereNamed').replace('{name}', () => s.targetName)
              : s.orgName
                ? t('auth.acceptInvite.p.whereNamed').replace('{name}', () => s.orgName)
                : t('auth.acceptInvite.p.whereKind').replace('{kind}', t(s.kindInLabel))
            const as = s.inviteeEmail
              ? t('auth.acceptInvite.p.as').replace('{email}', () => s.inviteeEmail)
              : ''
            return t('auth.acceptInvite.p.welcome')
              .replace('{who}', () => who)
              .replace('{where}', () => where)
              .replace('{as}', () => as)
          }
          if (s.status === 'mismatch') return s.message
          if (s.status === 'success' || s.status === 'error') return s.message
          if (s.status === 'redirect') return t('auth.acceptInvite.p.redirect')
          return t('auth.acceptInvite.p.checking').replace('{kind}', t(s.kindLabel))
        }
      }
    },

    // Signed-out welcome beat — create-account first (invites mostly
    // target new teammates), sign-in second. Both destinations carry the
    // token/return path computed in _runAcceptFlow.
    WelcomeActions: {
      flow: 'y',
      gap: 'Z1',
      width: '100%',
      align: 'center center',
      hide: (el, s) => s.status !== 'welcome',
      CreateAccountButton: {
        extends: ['WhitelabelAccent', 'Button'],
        theme: 'primary',
        type: 'button',
        minHeight: 'controlH',
        padding: '0 B',
        round: 'radiusPill',
        fontSize: 'Z2',
        fontWeight: '700',
        text: '{{ auth.acceptInvite.createAccount | polyglot }}',
        onClick: (ev, el, s) => {
          ev.preventDefault()
          el.router(s.signupPath || '/signup', el.getRoot())
        }
      },
      SignInButton: {
        extends: 'Button',
        type: 'button',
        isBranded: (el, s) => !!(s.root && s.root.whitelabelBrand),
        '.isBranded': { width: '100%', round: 'radiusControl', minHeight: 'controlH' },
        background: 'hover',
        color: 'paragraph',
        minHeight: 'controlHCompact',
        padding: '0 A2',
        round: 'radiusPill',
        fontSize: 'Z',
        text: '{{ auth.acceptInvite.haveAccount | polyglot }}',
        onClick: (ev, el, s) => {
          ev.preventDefault()
          el.router(s.loginPath || '/login', el.getRoot())
        }
      }
    },

    // Wrong-account beat — one action: switch accounts (sign out, then
    // back to sign-in with the invite as the return path).
    SwitchAccountButton: {
      extends: ['WhitelabelAccent', 'Button'],
      theme: 'primary',
      type: 'button',
      minHeight: 'controlH',
      padding: '0 B',
      round: 'radiusPill',
      fontSize: 'Z2',
      fontWeight: '700',
      text: '{{ auth.acceptInvite.switchAccount | polyglot }}',
      hide: (el, s) => s.status !== 'mismatch',
      onClick: async (ev, el, s) => {
        ev.preventDefault()
        try {
          if (typeof el.sdk?.logout === 'function') await el.sdk.logout()
        } catch (_) {
          /* a failed sign-out still lands on /login, which can switch */
        }
        el.router(s.loginPath || '/login', el.getRoot())
      }
    },

    CheckButton: {
      extends: ['WhitelabelAccent', 'CheckButton'],
      icon: null,
      type: 'button',
      minHeight: 'controlH',
      padding: '0 A',
      fontSize: 'Z2',
      fontWeight: '700',
      // Both labels were hardcoded English — the button every invitee presses
      // last read "Open workspace" in a Georgian session.
      text: (el, s) =>
        el.call(
          'polyglot',
          s.status === 'error'
            ? 'auth.acceptInvite.goToWorkspace'
            : 'auth.acceptInvite.openWorkspace'
        ),
      hide: (el, s) => s.status !== 'success' && s.status !== 'error',
      onClick: (ev, el, s) => {
        ev.preventDefault()
        if (typeof window === 'undefined') return
        // Land on the SAME destination the success path computed — the
        // joined workspace, not the generic dashboard. Success state means
        // the in-place switch already ran (or its fallback reload is
        // queued); the error state still needs the fresh-boot recovery.
        const dest = s.dest || '/'
        if (s.status === 'success') el.router(dest, el.getRoot())
        else window.location.replace(dest)
      }
    }
  }
}
